/**
 * Central catalog of user-facing error messaging for the failure page.
 *
 * The failure page is reached from two kinds of failures:
 *  1. API failures caught in page catch-blocks, where the caught
 *     {@link ExceptionDetail} carries an HTTP `errorStatus` (e.g. '403', '500').
 *  2. Route guards / uncaught render errors, which have no HTTP status and use
 *     the generic bucket.
 *
 * `resolveErrorClass` collapses a raw error status/code into one of the
 * {@link ErrorClass} buckets below, and `ERROR_CLASS_TO_DISPLAY` gives each
 * bucket a distinct, human-readable title + body so users get a clear message
 * instead of a single opaque "Something went wrong" for everything.
 */

export enum ErrorClass {
  UNAUTHENTICATED = 'unauthenticated', // 401
  ACCESS_DENIED = 'access-denied', // 403
  NOT_FOUND = 'not-found', // 404
  TIMEOUT = 'timeout', // 408 / network / no status
  THROTTLED = 'throttled', // 429
  SERVER_ERROR = 'server-error', // 5xx
  CONNECTIVITY = 'connectivity', // no response: network / CORS / unreachable
  GENERIC = 'generic' // anything else / uncaught
}

/**
 * PXT CMS on-call ticket-creation template. Rendered as a link on failure
 * classes where reaching out to on-call is the right escalation path, so users
 * cut a ticket with the right template instead of being told to "reach out".
 */
export const PXT_CMS_ONCALL_TICKET_URL =
  'https://t.corp.amazon.com/create/templates/4a282c19-147b-4d31-b7d4-747d4a001767';

export interface ErrorDisplay {
  title: string;
  messages: ReadonlyArray<string>;
  /**
   * When true, the failure page renders a link to {@link PXT_CMS_ONCALL_TICKET_URL}
   * so the user can cut an on-call ticket for this failure.
   */
  showOncallLink?: boolean;
}

export const ERROR_CLASS_TO_DISPLAY: Record<ErrorClass, ErrorDisplay> = {
  [ErrorClass.UNAUTHENTICATED]: {
    title: 'Your session has expired',
    messages: [
      'We could not verify your session. Please refresh the page and sign in again.',
      'If the problem persists, clear your Midway session and retry.'
    ]
  },
  [ErrorClass.ACCESS_DENIED]: {
    title: 'You do not have access to this',
    messages: [
      'Your profile does not have permission to view or manage this resource in this domain.',
      'Request access through the PXT CMS Admin Console onboarding process, then try again.'
    ]
  },
  [ErrorClass.NOT_FOUND]: {
    title: 'We could not find that',
    messages: [
      'The resource you are looking for does not exist or is not available in this domain.',
      'Check the domain and identifier, then try again.'
    ]
  },
  [ErrorClass.TIMEOUT]: {
    title: 'The request timed out',
    messages: [
      'The service took too long to respond or the network dropped the request.',
      'Please try again in a few moments.'
    ]
  },
  [ErrorClass.THROTTLED]: {
    title: 'The service is busy right now',
    messages: [
      'This request was throttled because the service is handling a high volume of traffic.',
      'Please wait a moment and try again.'
    ]
  },
  [ErrorClass.SERVER_ERROR]: {
    title: 'Something went wrong on our side',
    messages: [
      'A backend service returned an unexpected error while processing your request.',
      'Try again in a few moments.'
    ],
    showOncallLink: true
  },
  [ErrorClass.CONNECTIVITY]: {
    title: 'Could not reach the service',
    messages: [
      'The request could not reach the backend. This usually means a dropped ' +
        'connection, an expired session, or (on a dev build) that you are ' +
        'not on VPN or signed in to Midway.',
      'Check your connection and sign-in, then try again.'
    ],
    showOncallLink: true
  },
  [ErrorClass.GENERIC]: {
    title: 'Something went wrong',
    messages: [
      'The Admin Console hit an unexpected error and could not complete your request.',
      'Try again, or go back to the home page.'
    ],
    showOncallLink: true
  }
};

/**
 * Collapse a raw error status/code into an {@link ErrorClass}.
 *
 * @param rawStatusOrCode an HTTP status string/number (e.g. '403', 500) or a
 *   route error code such as `ErrorCode.GENERIC_FAILURE`. `undefined`/`null`
 *   maps to the GENERIC bucket, so callers should pass the real status (or the
 *   Axios transport code for status-less failures) when they have it — that is
 *   what routes a failure to a specific, actionable message.
 */
export const resolveErrorClass = (
  rawStatusOrCode?: string | number
): ErrorClass => {
  if (rawStatusOrCode === undefined || rawStatusOrCode === null) {
    return ErrorClass.GENERIC;
  }
  const value = rawStatusOrCode.toString();

  switch (value) {
    case '401':
      return ErrorClass.UNAUTHENTICATED;
    case '403':
      return ErrorClass.ACCESS_DENIED;
    case '404':
      return ErrorClass.NOT_FOUND;
    case '408':
      return ErrorClass.TIMEOUT;
    case '429':
      return ErrorClass.THROTTLED;
    // Connectivity: our own route code, plus the transport codes Axios sets
    // when no HTTP response is received (network drop / CORS / unreachable /
    // timeout).
    case 'connection-error':
    case 'ERR_NETWORK':
    case 'ECONNABORTED':
    case 'ETIMEDOUT':
    case 'ERR_CANCELED':
      return ErrorClass.CONNECTIVITY;
    default:
      break;
  }

  if (/^5\d{2}$/.test(value)) {
    return ErrorClass.SERVER_ERROR;
  }
  // Any other 4xx (e.g. a 400 validation error) must NOT fall into
  // ACCESS_DENIED, whose permission-specific "request access" copy would send
  // users down the wrong path. Unmatched statuses/codes use the neutral
  // GENERIC message.
  return ErrorClass.GENERIC;
};

export const getErrorDisplay = (
  rawStatusOrCode?: string | number
): ErrorDisplay => ERROR_CLASS_TO_DISPLAY[resolveErrorClass(rawStatusOrCode)];
